import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Shield, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SectionBubbles from '../components/SectionBubbles';
import SEO from '../components/SEO';
import { galleryImages, categories } from '../data/galleryImages';

const Gallery = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredImages = selectedCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="page-wrapper">
      <SEO 
        title={t('seo.gallery.title')}
        description={t('seo.gallery.description')}
        keywords={t('seo.gallery.keywords')}
        canonical="https://dsotocleaning.com/gallery"
      />
      
      <Navigation />
      
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{t('gallery.hero_title')}</h1>
          <p className="page-hero-description">{t('gallery.hero_desc')}</p>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <div className="gallery-filters">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`gallery-filter-btn ${selectedCategory === category ? 'active' : ''}`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="gallery-grid">
            {filteredImages.map((image) => (
              <div key={image.id} className="gallery-item">
                <img src={image.url} alt={image.title} />
                <div className="gallery-overlay">
                  <h3>{image.title}</h3>
                  <p>{image.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Identity Section */}
      <section className="brand-id-section section-padding" data-testid="brand-identity-section">
        <SectionBubbles />
        <div className="container">
          <h2 className="section-title text-center">Brand Identity: Professional Excellence in Every Detail</h2>
          <p className="section-description text-center centered-text mb-12">
            Operational excellence begins with how we present ourselves.
          </p>

          <div className="brand-id-grid">
            <div className="brand-id-visual">
              <div className="brand-id-logo-frame" data-testid="brand-logo-frame">
                <img
                  src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/km539nin_D%20Soto%20Cleaning%20Logo%20Color.png"
                  alt="D Soto Cleaning Official Logo"
                  className="brand-id-logo"
                />
              </div>
              <div className="brand-id-tagline">Operational Excellence, Everywhere</div>
            </div>

            <div className="brand-id-content">
              <p className="brand-id-lead">
                Our visual identity reflects our commitment to professional excellence 
                and corporate sophistication.
              </p>
              <p className="brand-id-text">
                Our color palette combines contemporary tones with traditional corporate blues 
                to convey innovation and reliability. The official logo remains unchanged and 
                serves as the cornerstone of all brand communications, ensuring instant 
                recognition and consistency across all client touchpoints.
              </p>
              <p className="brand-id-text">
                Our typography and visual standards create a cohesive, professional appearance 
                that resonates with executive decision-makers and procurement professionals.
              </p>

              <div className="brand-id-bullets">
                <div className="brand-id-bullet">
                  <Eye size={20} />
                  <span>Instant Recognition & Consistency</span>
                </div>
                <div className="brand-id-bullet">
                  <Shield size={20} />
                  <span>Corporate Sophistication & Trust</span>
                </div>
                <div className="brand-id-bullet">
                  <Users size={20} />
                  <span>Built for Enterprise Decision-Makers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Official Corporate Film */}
      <section className="film-section" data-testid="official-video-section">
        <div className="container">
          <div className="film-header">
            <h2 className="film-title">Watch Our Official Corporate Film</h2>
            <p className="film-subtitle">
              Built With Purpose. Captured With Precision.
            </p>
          </div>

          <div className="film-layout">
            <div className="film-video-wrap">
              <div className="video-responsive-wrap" data-testid="video-player">
                <iframe
                  src="https://www.youtube.com/embed/vU2XgybHDaE?rel=0&modestbranding=1&playsinline=1&showinfo=0&iv_load_policy=3&enablejsapi=1"
                  title="D Soto Cleaning - Official Corporate Film"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  playsInline
                />
              </div>
            </div>

            <div className="film-copy">
              <p className="film-lead">
                This is more than a company video. It is a reflection of who we are.
              </p>
              <p className="film-text">
                Months of preparation, operational coordination, and strategic planning went into 
                capturing the scale, discipline, and infrastructure that define D Soto Cleaning.
              </p>
              <p className="film-text">
                From high-volume fleet operations to the structured systems behind our 400+ workforce, 
                this film represents the standard we bring to every partnership.
              </p>
              <div className="film-cta-lines">
                <span>See the precision.</span>
                <span>See the scale.</span>
                <span>See the difference.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section section-padding">
        <div className="container text-center">
          <h2 className="section-title text-white">{t('gallery.cta_title')}</h2>
          <p className="section-description text-white centered-text mb-8">{t('gallery.cta_desc')}</p>
          <Link to="/contact">
            <Button size="lg" className="cta-button-white">
              {t('gallery.cta_btn')} <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Gallery;
